import React, { useState } from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Loader2,
  FileText,
  Upload,
  Sparkles,
  ShieldCheck,
  Activity,
} from "lucide-react";
import { BIKValidationResults } from "@/components/payroll-validator/BIKValidationResults";
import { FPSEmployeeAnalysis } from "@/components/payroll-validator/FPSEmployeeAnalysis";
import { BikResponse, FPSResponse } from "@/types/payroll-validator-demo";

const API_BASE = (() => {
  const fallback =
    process.env.NODE_ENV === "production"
      ? "https://api.hirelo.io"
      : "http://localhost:8000";
  const base = process.env.REACT_APP_API_URL || fallback;
  return base.endsWith("/") ? base.slice(0, -1) : base;
})();

const DEMO_ENDPOINTS = {
  bik: `${API_BASE}/api/payroll-validator/demo/validate-bik/`,
  fps: `${API_BASE}/api/payroll-validator/demo/parse-fps/`,
};

type FileKind = "bik" | "fps";

const detectFileKind = (file: File | null): FileKind | null => {
  if (!file) return null;
  const name = file.name.toLowerCase();
  const type = (file.type || "").toLowerCase();

  if (name.endsWith(".csv") || type.includes("csv")) return "bik";
  if (name.endsWith(".xml") || type.includes("xml")) return "fps";

  return null;
};

const describeFileKind = (file: File | null, kind: FileKind | null): string => {
  if (!file) return "Choose a CSV (.csv) or XML (.xml) file to begin.";
  if (kind === "bik") return "Detected CSV - we will run BIK validation.";
  if (kind === "fps")
    return "Detected XML - we will parse FPS employee details.";
  return "File type not recognized. Supported formats: .csv or .xml.";
};

const createProcessingDelay = () => {
  const minimumMs = 2000;
  const maximumMs = 3200;
  const duration = Math.floor(
    minimumMs + Math.random() * (maximumMs - minimumMs),
  );
  return new Promise((resolve) => setTimeout(resolve, duration));
};

export const PayrollValidatorDemoPage: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [previousFile, setPreviousFile] = useState<File | null>(null);
  const [detectedKind, setDetectedKind] = useState<FileKind | null>(null);
  const [lastRunKind, setLastRunKind] = useState<FileKind | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bikResponse, setBikResponse] = useState<BikResponse | null>(null);
  const [fpsResponse, setFpsResponse] = useState<FPSResponse | null>(null);

  const clearResults = () => {
    setBikResponse(null);
    setFpsResponse(null);
  };

  const handleProcess = async () => {
    if (!file) {
      setError("Please choose a CSV or XML file before running.");
      return;
    }

    const kind = detectFileKind(file);
    if (!kind) {
      setError("File must be CSV (.csv) or XML (.xml).");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    if (kind === "fps" && previousFile) {
      formData.append("previous_file", previousFile);
    }

    setLoading(true);
    setError(null);
    setLastRunKind(kind);
    clearResults();

    const processingDelay = createProcessingDelay();
    const endpoint = kind === "bik" ? DEMO_ENDPOINTS.bik : DEMO_ENDPOINTS.fps;
    const failureMessage =
      kind === "bik"
        ? "Failed to validate BIK data."
        : "Failed to parse FPS XML.";

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        body: formData,
        credentials: "omit",
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        const detail = payload.error || response.statusText;
        throw new Error(detail || failureMessage);
      }

      const data = (await response.json()) as BikResponse | FPSResponse;
      await processingDelay;
      if (kind === "bik") {
        setBikResponse(data as BikResponse);
      } else {
        setFpsResponse(data as FPSResponse);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : failureMessage;
      await processingDelay;
      setError(message);
      setLastRunKind(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-b from-[#050506] via-[#0a0c14] to-[#050506] text-slate-100">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-40 -top-28 h-96 w-96 rounded-full bg-orange-500/25 blur-3xl" />
        <div className="absolute right-0 top-20 h-96 w-96 rounded-full bg-indigo-500/20 blur-3xl" />
        <div className="absolute inset-x-10 top-1/2 h-1/3 rounded-full bg-white/5 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-6xl px-6 py-12 space-y-10">
        <div className="grid items-start gap-10 lg:grid-cols-2">
          <section className="space-y-6">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-orange-200">
              <Sparkles className="h-4 w-4 text-orange-300" />
              Parallel Runs
            </div>

            <header className="space-y-4">
              <h1 className="text-4xl font-bold leading-tight text-white md:text-5xl">
                Simulate Payroll Accurately
              </h1>
              <p className="text-lg text-slate-300">
                Run side-by-side comparisons between your current payroll system
                and our engine in minutes. Eliminate onboarding uncertainty,
                reduce implementation risk, and ensure your payroll
                configuration is correct before go-live.
              </p>
              <div className="flex flex-wrap gap-3">
                <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-sm text-slate-100">
                  Compliant
                </span>
                <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-sm text-slate-100">
                  100% Accuracy
                </span>
                <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-sm text-slate-100">
                  HMRC Approved
                </span>
              </div>
            </header>

            <div className="grid gap-4 sm:grid-cols-3">
              <div className="rounded-2xl border border-white/5 bg-white/5 p-4">
                <div className="flex items-center gap-2 text-sm text-orange-100">
                  <FileText className="h-4 w-4 text-orange-300" />
                  Auto-detect format
                </div>
                <p className="mt-2 text-sm text-slate-300">
                  CSV and FPS XML are recognised automatically so the right
                  check runs every time.
                </p>
              </div>
              <div className="rounded-2xl border border-white/5 bg-white/5 p-4">
                <div className="flex items-center gap-2 text-sm text-indigo-100">
                  <Activity className="h-4 w-4 text-indigo-300" />
                  BIK + FPS insights
                </div>
                <p className="mt-2 text-sm text-slate-300">
                  See tax code, NI letter, pension type and BIK gaps instantly
                  for every employee.
                </p>
              </div>
              <div className="rounded-2xl border border-white/5 bg-white/5 p-4">
                <div className="flex items-center gap-2 text-sm text-emerald-100">
                  <ShieldCheck className="h-4 w-4 text-emerald-300" />
                  Explainable results
                </div>
                <p className="mt-2 text-sm text-slate-300">
                  Findings are highlighted with clear descriptions so you can
                  act with confidence.
                </p>
              </div>
            </div>
          </section>

          <section className="space-y-4">
            <Card className="border-white/10 bg-[#0d0f16]/80 text-slate-100 shadow-2xl backdrop-blur">
              <CardHeader className="space-y-1">
                <CardTitle className="flex items-center gap-2 text-xl text-white">
                  <FileText className="h-5 w-5 text-orange-300" />
                  Upload any Payroll Data
                </CardTitle>
                <CardDescription className="text-sm text-slate-300">
                  We detect the format and route it to the right validator
                  automatically.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="space-y-4 rounded-xl border border-dashed border-white/15 bg-white/5 p-4">
                  <Label
                    htmlFor="payroll-file"
                    className="text-sm text-slate-200"
                  >
                    Payroll file
                  </Label>
                  <Input
                    id="payroll-file"
                    type="file"
                    accept=".csv,text/csv,.xml,text/xml,application/xml"
                    className="!mb-4 !h-12 !py-2 cursor-pointer border-white/10 bg-[#0b0d13] text-slate-100 file:mr-4 file:h-full file:cursor-pointer file:rounded-md file:border-0 file:bg-white/10 file:px-4 file:text-sm file:text-slate-100"
                    onChange={(event) => {
                      const nextFile = event.target.files?.[0] ?? null;
                      setFile(nextFile);
                      const kind = detectFileKind(nextFile);
                      setDetectedKind(kind);
                      setLastRunKind(null);
                      setError(null);
                      clearResults();
                      // Clear previous file if switching away from FPS
                      if (kind !== "fps") {
                        setPreviousFile(null);
                      }
                    }}
                  />
                  <p className="text-sm text-slate-400">
                    {describeFileKind(file, detectedKind)}
                  </p>
                </div>

                {detectedKind === "fps" && (
                  <div className="space-y-4 rounded-xl border border-dashed border-white/15 bg-white/5 p-4">
                    <Label
                      htmlFor="previous-fps-file"
                      className="text-sm text-slate-200"
                    >
                      Previous Month FPS (optional)
                    </Label>
                    <Input
                      id="previous-fps-file"
                      type="file"
                      accept=".xml,text/xml,application/xml"
                      className="!mb-4 !h-12 !py-2 cursor-pointer border-white/10 bg-[#0b0d13] text-slate-100 file:mr-4 file:h-full file:cursor-pointer file:rounded-md file:border-0 file:bg-white/10 file:px-4 file:text-sm file:text-slate-100"
                      onChange={(event) => {
                        const nextFile = event.target.files?.[0] ?? null;
                        setPreviousFile(nextFile);
                      }}
                    />
                    <p className="text-sm text-slate-400">
                      {previousFile
                        ? `Previous month: ${previousFile.name}`
                        : "Upload last month's FPS to detect BIK changes."}
                    </p>
                  </div>
                )}

                {error && (
                  <Alert className="border border-red-500/30 bg-red-500/10 text-red-100">
                    <AlertTitle className="text-red-100">
                      Unable to process
                    </AlertTitle>
                    <AlertDescription className="text-red-100/90">
                      {error}
                    </AlertDescription>
                  </Alert>
                )}

                <div className="flex flex-col gap-3 sm:flex-row">
                  <Button
                    onClick={handleProcess}
                    disabled={loading || !file}
                    className="flex-1 bg-gradient-to-r from-amber-300 via-orange-400 to-amber-500 text-slate-900 shadow-lg shadow-orange-500/20 hover:from-amber-200 hover:via-orange-300 hover:to-amber-400 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Upload className="mr-2 h-4 w-4" />
                        Validate Data
                      </>
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    className="border border-white/10 bg-white/5 text-slate-100 hover:bg-white/10"
                    onClick={() => {
                      setFile(null);
                      setPreviousFile(null);
                      setDetectedKind(null);
                      setLastRunKind(null);
                      setError(null);
                      clearResults();
                    }}
                  >
                    Clear
                  </Button>
                </div>
              </CardContent>
            </Card>

            <div className="rounded-2xl border border-white/10 bg-white/5 p-4 shadow-lg">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-slate-400">
                    Output
                  </p>
                  <p className="text-base font-semibold text-white">
                    {lastRunKind === "bik" && "BIK validation results"}
                    {lastRunKind === "fps" && "FPS employee analysis"}
                    {!lastRunKind && "Upload a file to see analysis"}
                  </p>
                </div>
                <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-slate-200">
                  {lastRunKind
                    ? `Detected: ${lastRunKind.toUpperCase()}`
                    : "Awaiting file"}
                </span>
              </div>

              <div className="mt-4 space-y-4">
                {lastRunKind === "bik" && (
                  <BIKValidationResults response={bikResponse} />
                )}
                {lastRunKind === "fps" && (
                  <FPSEmployeeAnalysis response={fpsResponse} />
                )}
                {!lastRunKind && (
                  <p className="text-sm text-slate-400">
                    We will surface detailed findings here once you upload a CSV
                    or FPS XML file.
                  </p>
                )}
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PayrollValidatorDemoPage;
